import { Link } from "react-router-dom";
import { Award, Users, Wrench, ShieldCheck, ArrowUpRight } from "lucide-react";

import PageHero from "@/components/PageHero";
import StatsCounter from "@/components/StatsCounter";
import Seo from "@/components/Seo";
import { STATS, COMPANY } from "@/lib/site";

const HERO_IMG =
  "/images/vmec2zqr.png";
const SHOP_IMG =
  "/images/dlpqse35.png";

const VALUES = [
  {
    icon: Wrench,
    title: "Craft over speed",
    body: "We staff every project with manufacturer-certified applicators and don't sub out our crews. The hand on the trowel matters.",
  },
  {
    icon: ShieldCheck,
    title: "Spec-driven systems",
    body: "We engineer to your operating conditions — chemical exposure, thermal cycling, OSHA traction targets, USDA wash-down — not a brochure SKU.",
  },
  {
    icon: Users,
    title: "Built around your shutdown",
    body: "Weekend installs, phased night work, vapor containment. Our schedule bends so your production calendar doesn't.",
  },
  {
    icon: Award,
    title: "10-year-system warranties",
    body: "Backed by manufacturer warranties when paired with our specified surface prep — written into every proposal.",
  },
];

export default function About() {
  return (
    <div data-testid="page-about">
      <Seo
        title="About"
        description="Vision Era Construction is a Greater Toronto Area specialty contractor focused on long-life industrial surfaces and exterior envelopes."
        path="/about"
      />
      <PageHero
        eyebrow="About Vision Era"
        title="Industrial trades, modern standards."
        subtitle="Vision Era Construction is a Greater Toronto Area specialty contractor focused on long-life industrial surfaces — the floors and façades that keep the operations the rest of the city depends on running."
        image={HERO_IMG}
      />

      <section className="section-pad">
        <div className="container-max grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <div className="eyebrow">Our Story</div>
            <h2 className="font-display text-5xl lg:text-6xl uppercase tracking-wider text-zinc-50 mt-4 leading-[0.95]">
              A crew &amp; a grinder — building the GTA&apos;s toughest floors.
            </h2>
            <div className="space-y-5 text-zinc-400 leading-relaxed mt-8">
              <p>
                Vision Era was started by tradespeople who got tired of watching
                two-year coatings sold as ten-year solutions. We service the
                Greater Toronto Area — Toronto, Mississauga, Brampton, Vaughan,
                Markham, Oakville and every industrial corridor in between.
              </p>
              <p>
                Across food processing plants, dairies, breweries, auto
                manufacturing, distribution hubs, pharmaceutical cleanrooms and
                premium residential exteriors, we&apos;ve installed millions of
                square feet of high-performance flooring and envelope systems.
              </p>
              <p>
                Every job starts the same way — substrate testing, moisture
                readings, profile measurement, and a written spec. Then we
                mobilize and execute, on schedule, with crews that have been
                with us for years.
              </p>
            </div>
          </div>

          <div className="lg:col-span-6">
            <div
              className="aspect-square bg-cover bg-center border border-zinc-800"
              style={{ backgroundImage: `url(${SHOP_IMG})` }}
            />
          </div>
        </div>
      </section>

      <StatsCounter stats={STATS} />

      <section className="section-pad" data-testid="values">
        <div className="container-max">
          <div className="eyebrow">What we stand on</div>
          <h2 className="font-display text-5xl lg:text-6xl uppercase tracking-wider text-zinc-50 mt-4 max-w-3xl">
            Four standards we won&apos;t cut.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-14">
            {VALUES.map((v, i) => {
              const Icon = v.icon;
              return (
                <div
                  key={v.title}
                  data-testid={`value-${i}`}
                  className="bg-zinc-900 border border-zinc-800 p-8 lg:p-10 hover:border-[#FF5A00] transition-colors"
                >
                  <div className="w-12 h-12 grid place-items-center bg-[#FF5A00]/10 border border-[#FF5A00]/40 mb-6">
                    <Icon className="w-5 h-5 text-[#FF5A00]" />
                  </div>
                  <h3 className="font-display text-3xl tracking-wider uppercase text-zinc-50">
                    {v.title}
                  </h3>
                  <p className="text-zinc-400 mt-3 leading-relaxed">{v.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="section-pad bg-zinc-900 border-y border-zinc-800">
        <div className="container-max flex flex-col lg:flex-row items-start lg:items-end justify-between gap-8">
          <div>
            <div className="eyebrow">Certifications</div>
            <h2 className="font-display text-5xl uppercase tracking-wider text-zinc-50 mt-4 max-w-2xl">
              Licensed, bonded, audit-ready.
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 w-full lg:w-auto">
            {["WSIB", "Food-Safety", "Working at Heights", "AMPP Cert."].map((c) => (
              <div
                key={c}
                className="border border-zinc-800 px-5 py-6 bg-zinc-950 text-center"
              >
                <div className="font-display text-2xl tracking-wider text-zinc-50">
                  {c}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-pad">
        <div className="container-max text-center">
          <h2 className="font-display text-5xl lg:text-7xl uppercase tracking-wider text-zinc-50">
            Have a project that
            <br />
            can&apos;t fail?
          </h2>
          <p className="text-zinc-400 mt-6 max-w-2xl mx-auto text-lg">
            Talk to our estimating team. We&apos;ll review your specs, your
            timing, and your exposure conditions — and recommend the right
            system before a single bucket lands on site.
          </p>
          <Link to="/contact" data-testid="about-final-cta" className="btn-primary mt-10">
            Request a Quote <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
